/**
 * The Gauge: classic 270° dials on one canvas, shared by Explore, dashboard
 * tiles and exports - one canvas, as Number is, so a chart image, a dashboard
 * image and a Catalog thumbnail all have something to copy.
 *
 * Several ships are laid out here rather than as grid panels: by ship (a panel
 * of dials per ship), by sensor (a panel per dial, the ships inside) or as a
 * matrix. Sizes are chosen to make the dials as large as the box allows; small
 * dials drop their tick numbers before anything else, never the reading.
 */
import uPlot from 'uplot';
import type { GaugeDialResult, GaugeResponse } from '../../../api/types.ts';
import { plotState, type PlotState } from '../../../state/plot-state.ts';
import { shipLabel } from '../../../utils/ships.ts';
import { colorLegend } from '../plot-ui-state.ts';
import {
  dialKey,
  dialRange,
  dialTitle,
  gaugeScale,
  tickDecimals,
  tickText,
  zoneAt,
  type GaugeDialState,
  type GaugeLayout,
} from '../gauge-config.ts';
import { formatNumber } from './render-number.ts';
import { sliceGauge } from '../gauge-slice.ts';
import { chartContainerSize, getCssVar, themeColors, type ThemeColors } from './chart-dom.ts';
import { rebuildUplot } from './uplot-helpers.ts';

/* ── What each dial shows ───────────────────────────────── */

export type DialStatus = 'ok' | 'empty' | 'missing' | 'failed' | 'unplotted';

export interface DialReading {
  value: number | null;
  status: DialStatus;
  /** Signals this ship lacks, for `missing`. */
  missing?: string[];
}

export interface GaugeRow {
  /** Null for a one-ship gauge, which needs no ship names. */
  ship: string | null;
  readings: DialReading[];
  error?: string;
}

/**
 * Match loaded values to the dials as they are now, by what each dial reads
 * (`dialKey`), so reordering or removing a dial redraws without a fetch and a
 * dial added since the last Plot says so rather than borrowing a neighbour's
 * value.
 */
export function readRows(data: GaugeResponse, dials: GaugeDialState[]): GaugeRow[] {
  const rows = data.ships ?? [{ ship: null as string | null, dials: data.dials as GaugeDialResult[] | null }];
  return rows.map((row) => {
    const error = 'error' in row ? (row as { error?: string }).error : undefined;
    const used = new Set<number>();
    const readings = dials.map((d): DialReading => {
      if (!row.dials) return { value: null, status: 'failed' };
      const key = dialKey(d);
      // Each loaded value goes to one dial; a second dial reading the same
      // thing (a duplicate) shares it, since it would compute the same number.
      let i = row.dials.findIndex((r, j) => !used.has(j) && r.key === key);
      if (i < 0) i = row.dials.findIndex((r) => r.key === key);
      if (i < 0) return { value: null, status: 'unplotted' };
      used.add(i);
      const r = row.dials[i]!;
      if (r.missing?.length) return { value: null, status: 'missing', missing: r.missing };
      if (r.value == null || !Number.isFinite(r.value)) return { value: null, status: 'empty' };
      return { value: r.value, status: 'ok' };
    });
    return error ? { ship: row.ship, readings, error } : { ship: row.ship, readings };
  });
}

/* ── Layout ─────────────────────────────────────────────── */

/** The dial's drawing box, in its own units: 200 wide. */
const DIAL_W = 200;
// The reading sits below the ends of the arc, clear of the end numbers, and
// the label below it.
const DIAL_H = 180;
const LABEL_H = 22;
const CAPTION_H = 18;

export interface Rect { x: number; y: number; w: number; h: number }

export interface DialCell {
  rect: Rect;
  dial: number;
  row: number;
  /** Draw the dial's label under the reading. */
  label: boolean;
  /** A ship name above the dial (by sensor). */
  caption: string | null;
}

export interface GaugeLayoutResult {
  cells: DialCell[];
  panels: Array<{ rect: Rect; title: string; sub?: string | undefined }>;
  headers: Array<{ x: number; y: number; w: number; text: string; sub?: string | undefined }>;
  rowNames: Array<{ x: number; y: number; w: number; text: string }>;
  notes: Array<{ rect: Rect; text: string }>;
}

interface Fit { cols: number; rows: number; size: number }

/** Columns for `n` items of height `aspect` x width in a box, largest items first. */
export function fitGrid(w: number, h: number, n: number, aspect: number, gap: number): Fit {
  let best: Fit = { cols: 1, rows: n, size: 0 };
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const size = Math.min((w - gap * (cols - 1)) / cols, (h - gap * (rows - 1)) / rows / aspect);
    // Ties go to fewer empty slots, then fewer columns.
    if (size > best.size + 0.5) best = { cols, rows, size };
  }
  best.size = Math.max(0, best.size);
  return best;
}

export interface LayoutMetrics {
  /** One CSS pixel in canvas pixels. */
  px: number;
  titleH: number;
  pad: number;
  gap: number;
  /** Width of the widest ship name, for the matrix. */
  nameW: number;
}

/** Place items of `aspect` in `box`, centred, at most `maxSize` wide. */
function placeGrid(box: Rect, n: number, aspect: number, gap: number, maxSize = Infinity): Rect[] {
  const fit = fitGrid(box.w, box.h, n, aspect, gap);
  const size = Math.min(fit.size, maxSize);
  const cw = size;
  const ch = size * aspect;
  const usedW = fit.cols * cw + (fit.cols - 1) * gap;
  const usedH = fit.rows * ch + (fit.rows - 1) * gap;
  const x0 = box.x + (box.w - usedW) / 2;
  const y0 = box.y + (box.h - usedH) / 2;
  return Array.from({ length: n }, (_, i) => ({
    x: x0 + (i % fit.cols) * (cw + gap),
    y: y0 + Math.floor(i / fit.cols) * (ch + gap),
    w: cw,
    h: ch,
  }));
}

/**
 * Panels of items: pick the panel columns and, inside, the item columns that
 * make the items largest.
 */
function placePanels(
  box: Rect, panels: number, items: number, aspect: number, m: LayoutMetrics,
): Array<{ panel: Rect; inner: Rect }> {
  let best = { size: -1, pc: 1 };
  for (let pc = 1; pc <= panels; pc++) {
    const pr = Math.ceil(panels / pc);
    const pw = (box.w - m.gap * (pc - 1)) / pc;
    const ph = (box.h - m.gap * (pr - 1)) / pr;
    const size = fitGrid(pw - 2 * m.pad, ph - m.titleH - m.pad, items, aspect, m.gap).size;
    if (size > best.size + 0.5) best = { size, pc };
  }
  const pc = best.pc;
  const pr = Math.ceil(panels / pc);
  const pw = (box.w - m.gap * (pc - 1)) / pc;
  const ph = (box.h - m.gap * (pr - 1)) / pr;
  return Array.from({ length: panels }, (_, i) => {
    const panel = { x: box.x + (i % pc) * (pw + m.gap), y: box.y + Math.floor(i / pc) * (ph + m.gap), w: pw, h: ph };
    return {
      panel,
      inner: { x: panel.x + m.pad, y: panel.y + m.titleH, w: pw - 2 * m.pad, h: ph - m.titleH - m.pad },
    };
  });
}

/**
 * The dials a ship has: one lacking a signal (four engines of six) is left out
 * and the rest grow. If the ship has none of them, all stay to say so.
 */
function keptDials(row: GaugeRow, nd: number): number[] {
  const all = Array.from({ length: nd }, (_, dial) => dial);
  const kept = all.filter((dial) => row.readings[dial]?.status !== 'missing');
  return kept.length ? kept : all;
}

export function layoutGauge(
  box: Rect,
  dials: GaugeDialState[],
  rows: GaugeRow[],
  layout: GaugeLayout,
  m: LayoutMetrics,
): GaugeLayoutResult {
  const out: GaugeLayoutResult = { cells: [], panels: [], headers: [], rowNames: [], notes: [] };
  const nd = dials.length;
  const withLabel = (DIAL_H + LABEL_H) / DIAL_W;
  const bare = DIAL_H / DIAL_W;
  if (!nd || !rows.length) return out;
  // One ship: its dials, nothing else to name.
  if (rows.length === 1 && rows[0]!.ship === null) {
    const kept = keptDials(rows[0]!, nd);
    placeGrid(box, kept.length, withLabel, m.gap).forEach((rect, i) =>
      out.cells.push({ rect, dial: kept[i]!, row: 0, label: true, caption: null }));
    return out;
  }
  const names = rows.map((r) => (r.ship ? shipLabel(r.ship) : ''));
  if (layout === 'sensor') {
    const aspect = (DIAL_H + CAPTION_H) / DIAL_W;
    // A dial no ship has gets no panel; a ship without the dial gets no cell in it.
    const has = (row: number, dial: number): boolean => rows[row]!.readings[dial]?.status !== 'missing';
    const shown = dials.map((_, dial) => rows.map((_, row) => row).filter((row) => has(row, dial)));
    let panelDials = dials.map((_, dial) => dial).filter((dial) => shown[dial]!.length);
    if (!panelDials.length) panelDials = dials.map((_, dial) => dial);
    const most = Math.max(...panelDials.map((dial) => shown[dial]!.length || rows.length));
    placePanels(box, panelDials.length, most, aspect, m).forEach(({ panel, inner }, i) => {
      const dial = panelDials[i]!;
      const d = dials[dial]!;
      const inPanel = shown[dial]!.length ? shown[dial]! : rows.map((_, row) => row);
      out.panels.push({ rect: panel, title: dialTitle(d), sub: d.unit.trim() || undefined });
      placeGrid(inner, inPanel.length, aspect, m.gap).forEach((rect, j) =>
        out.cells.push({ rect, dial, row: inPanel[j]!, label: false, caption: names[inPanel[j]!]! }));
    });
    return out;
  }
  if (layout === 'matrix') {
    const nameW = Math.min(m.nameW + m.pad, box.w * 0.3);
    const grid = { x: box.x + nameW, y: box.y + m.titleH, w: box.w - nameW, h: box.h - m.titleH };
    const size = Math.max(0, Math.min(
      (grid.w - m.gap * (nd - 1)) / nd,
      (grid.h - m.gap * (rows.length - 1)) / rows.length / bare,
    ));
    const cw = size;
    const ch = size * bare;
    const usedW = nd * cw + (nd - 1) * m.gap;
    const x0 = grid.x + (grid.w - usedW) / 2;
    dials.forEach((d, dial) => out.headers.push({
      x: x0 + dial * (cw + m.gap), y: box.y, w: cw, text: dialTitle(d), sub: d.unit.trim() || undefined,
    }));
    rows.forEach((_, row) => {
      const y = grid.y + row * (ch + m.gap);
      out.rowNames.push({ x: box.x, y: y + ch / 2, w: nameW - m.pad, text: names[row]! });
      for (let dial = 0; dial < nd; dial++) {
        out.cells.push({ rect: { x: x0 + dial * (cw + m.gap), y, w: cw, h: ch }, dial, row, label: false, caption: null });
      }
    });
    return out;
  }
  // By ship.
  const kept = rows.map((r) => keptDials(r, nd));
  placePanels(box, rows.length, Math.max(...kept.map((k) => k.length)), withLabel, m).forEach(({ panel, inner }, row) => {
    out.panels.push({ rect: panel, title: names[row]! });
    const r = rows[row]!;
    if (r.error) { out.notes.push({ rect: inner, text: r.error }); return; }
    const k = kept[row]!;
    placeGrid(inner, k.length, withLabel, m.gap).forEach((rect, i) =>
      out.cells.push({ rect, dial: k[i]!, row, label: true, caption: null }));
  });
  return out;
}

/* ── Drawing ────────────────────────────────────────────── */

interface GaugeColors extends ThemeColors {
  track: string;
  border: string;
  faint: string;
  yellowBand: string;
  yellowText: string;
  red: string;
}

function gaugeColors(c: ThemeColors): GaugeColors {
  return {
    ...c,
    track: getCssVar('--surface2') || '#292c31',
    border: getCssVar('--border') || '#393c43',
    faint: getCssVar('--text-faint') || '#5b606b',
    yellowBand: getCssVar('--zone-yellow') || '#e0a93a',
    yellowText: getCssVar('--warning') || '#e0a93a',
    red: getCssVar('--danger') || '#f0686b',
  };
}

const A0 = -135;
const A1 = 135;
const rad = (deg: number): number => (deg * Math.PI) / 180;

/** Fit text into `max` wide, cutting with an ellipsis. */
function clipText(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(`${text.slice(0, mid)}…`).width <= max) lo = mid; else hi = mid - 1;
  }
  return lo ? `${text.slice(0, lo)}…` : '';
}

const STATUS_TEXT: Record<Exclude<DialStatus, 'ok'>, string> = {
  empty: 'No data',
  missing: 'Not on this ship',
  failed: 'No data',
  unplotted: 'Plot to show',
};

export function drawDial(
  ctx: CanvasRenderingContext2D,
  cell: DialCell,
  dial: GaugeDialState,
  reading: DialReading,
  range: { min: number; max: number } | null,
  col: GaugeColors,
  fonts: { sans: string; mono: string },
  px: number,
): void {
  const { rect } = cell;
  const top = cell.caption ? CAPTION_H : 0;
  const unitsH = DIAL_H + top + (cell.label ? LABEL_H : 0);
  const k = Math.min(rect.w / DIAL_W, rect.h / unitsH);
  if (k <= 0) return;
  const ox = rect.x + (rect.w - DIAL_W * k) / 2;
  const oy = rect.y + (rect.h - unitsH * k) / 2;
  const cx = ox + 100 * k;
  const cy = oy + (top + 92) * k;
  const r = 84 * k;
  const P = (radius: number, a: number): [number, number] => [cx + radius * Math.sin(rad(a)), cy - radius * Math.cos(rad(a))];
  const arc = (radius: number, a: number, b: number, color: string, width: number): void => {
    if (b - a < 0.05) return;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, rad(a - 90), rad(b - 90));
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.stroke();
  };
  const tick = (a: number, r0: number, r1: number, color: string, width: number): void => {
    const [x0, y0] = P(r0, a);
    const [x1, y1] = P(r1, a);
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.stroke();
  };
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineCap = 'butt';

  if (cell.caption) {
    ctx.font = `600 ${Math.max(9 * px, 12 * k)}px ${fonts.sans}`;
    ctx.fillStyle = col.textStrong;
    ctx.fillText(clipText(ctx, cell.caption, DIAL_W * k), cx, oy + (CAPTION_H / 2) * k);
  }

  const live = reading.status === 'ok';
  // A dial with nothing to read is drawn faint: the scale still says what it
  // would show, and the words say why it does not.
  if (!live) ctx.globalAlpha = 0.45;
  const band = 7 * k;
  arc(r - 4 * k, A0, A1, col.track, band);
  let zone = null as ReturnType<typeof zoneAt>;
  if (range) {
    const { min, max } = range;
    const frac = (v: number): number => (Math.min(max, Math.max(min, v)) - min) / (max - min);
    const ang = (v: number): number => A0 + (A1 - A0) * frac(v);
    // Yellow first, so red is on top where they overlap.
    for (const z of [...dial.zones].sort((a, b) => Number(a.color === 'red') - Number(b.color === 'red'))) {
      const from = z.from ?? min;
      const to = z.to ?? max;
      if (to > from) arc(r - 4 * k, ang(from), ang(to), z.color === 'red' ? col.red : col.yellowBand, band);
    }
    const sc = gaugeScale(min, max, dial.steps);
    // Tick numbers and the label stop growing on a large dial; the reading does not.
    const tickFont = Math.min(12 * k, 16 * px);
    if (tickFont >= 4 * px) for (const v of sc.minors) tick(ang(v), r - 1 * k, r - 6 * k, col.faint, Math.max(px * 0.75, 1 * k));
    // Numbers thin out on a small dial, then go; the ends always stay while any show.
    const every = tickFont < 9 * px || sc.majors.length > 7 ? 2 : 1;
    const labelled = (i: number): boolean => !(i % every) || i === sc.majors.length - 1;
    const dp = tickDecimals(sc.majors.filter((_, i) => labelled(i)), sc);
    ctx.font = `500 ${tickFont}px ${fonts.sans}`;
    ctx.fillStyle = col.text;
    sc.majors.forEach((v, i) => {
      tick(ang(v), r - 1 * k, r - 13 * k, col.textStrong, Math.max(px, 2 * k));
      if (tickFont < 7 * px || !labelled(i)) return;
      const [x, y] = P(r - 26 * k, ang(v));
      ctx.fillText(tickText(v, sc, dp), x, y);
    });
    if (live && reading.value != null) {
      zone = zoneAt(dial.zones, min, max, reading.value);
      const v = reading.value;
      // Past the scale the needle rests just beyond the end stop; the reading
      // below still says the true value.
      const a = v > max ? A1 + 4 : v < min ? A0 - 4 : ang(v);
      const pts = [P(r - 10 * k, a), P(3 * k, a + 90), P(14 * k, a + 180), P(3 * k, a - 90)];
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.closePath();
      ctx.fillStyle = col.textStrong;
      ctx.fill();
    }
  }
  ctx.beginPath();
  ctx.arc(cx, cy, 7 * k, 0, Math.PI * 2);
  ctx.fillStyle = live ? col.textStrong : col.faint;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, 2.4 * k, 0, Math.PI * 2);
  ctx.fillStyle = col.surface;
  ctx.fill();
  ctx.globalAlpha = 1;

  const readY = cy + 76 * k;
  const maxW = 170 * k;
  if (!range && live) {
    ctx.font = `500 ${Math.max(9 * px, 12 * k)}px ${fonts.sans}`;
    ctx.fillStyle = col.red;
    ctx.fillText(clipText(ctx, 'Maximum must be above minimum', DIAL_W * k), cx, readY);
  } else if (live && reading.value != null) {
    const num = formatNumber(reading.value, dial.decimals);
    const unit = dial.unit.trim();
    const unitFont = `500 ${Math.max(8 * px, 12 * k)}px ${fonts.sans}`;
    ctx.font = unitFont;
    const unitW = unit ? ctx.measureText(unit).width + 4 * k : 0;
    let size = 22 * k;
    ctx.font = `600 ${size}px ${fonts.mono}`;
    const numW = ctx.measureText(num).width;
    if (numW + unitW > maxW && numW > 0) size *= Math.max(0.3, (maxW - unitW) / numW);
    size = Math.max(size, 9 * px);
    ctx.font = `600 ${size}px ${fonts.mono}`;
    const w1 = ctx.measureText(num).width;
    const x0 = cx - (w1 + unitW) / 2;
    ctx.textAlign = 'left';
    ctx.fillStyle = zone === 'red' ? col.red : zone === 'yellow' ? col.yellowText : col.textStrong;
    ctx.fillText(num, x0, readY);
    if (unit) {
      ctx.font = unitFont;
      ctx.fillStyle = col.text;
      ctx.fillText(unit, x0 + w1 + 4 * k, readY + size * 0.08);
    }
    ctx.textAlign = 'center';
  } else if (reading.status !== 'ok') {
    ctx.font = `500 ${Math.max(9 * px, 13 * k)}px ${fonts.sans}`;
    ctx.fillStyle = col.text;
    ctx.fillText(clipText(ctx, STATUS_TEXT[reading.status], DIAL_W * k * 0.9), cx, readY);
  }
  if (cell.label) {
    ctx.font = `400 ${Math.max(8 * px, Math.min(12 * k, 16 * px))}px ${fonts.sans}`;
    ctx.fillStyle = col.text;
    ctx.fillText(clipText(ctx, dialTitle(dial), DIAL_W * k * 0.95), cx, cy + 100 * k);
  }
  ctx.restore();
}

/** What the gauge says, for a screen reader and the hover title. */
export function describeReadings(dials: GaugeDialState[], rows: GaugeRow[]): string {
  return rows.map((row) => {
    const parts = dials.map((d, i) => {
      const r = row.readings[i]!;
      const unit = d.unit.trim();
      return `${dialTitle(d)} ${r.status === 'ok' && r.value != null
        ? `${formatNumber(r.value, d.decimals)}${unit ? ` ${unit}` : ''}`
        : STATUS_TEXT[r.status as Exclude<DialStatus, 'ok'>]}`;
    });
    return `${row.ship ? `${shipLabel(row.ship)}: ` : ''}${parts.join(', ')}`;
  }).join('; ');
}

export function buildGaugeOpts(
  data: GaugeResponse,
  c: ThemeColors,
  size: { width: number; height: number },
  state: PlotState = plotState.value,
  /** Explore draws its watermark bottom-right; keep that strip clear of dials. */
  brandSpace = false,
  /**
   * The unsliced response, when `data` is a slice of it: Auto scale ends come
   * from the whole range, so they hold still while a slicer moves the needles.
   */
  whole: GaugeResponse = data,
): { opts: uPlot.Options; uData: uPlot.AlignedData } {
  const dials = state.gauge_dials;
  const rows = readRows(data, dials);
  const wholeRows = whole === data ? rows : readRows(whole, dials);
  const ranges = dials.map((d, i) => dialRange(d, wholeRows.map((r) => r.readings[i]!.value)));
  const col = gaugeColors(c);
  const fonts = { sans: getCssVar('--font-sans') || 'sans-serif', mono: getCssVar('--font-mono') || 'monospace' };
  const draw = (u: uPlot): void => {
    const ctx = u.ctx;
    const px = uPlot.pxRatio;
    const { left, top, width, height } = u.bbox;
    const fs = state.style.font_size * px;
    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, width, height);
    ctx.clip();
    ctx.font = `600 ${fs}px ${fonts.sans}`;
    const nameW = Math.max(0, ...rows.map((r) => (r.ship ? ctx.measureText(shipLabel(r.ship)).width : 0)));
    const m: LayoutMetrics = { px, titleH: fs * 2.2, pad: 8 * px, gap: 10 * px, nameW };
    const box = { x: left + 4 * px, y: top + 4 * px, w: width - 8 * px, h: height - 8 * px - (brandSpace ? 32 * px : 0) };
    const lay = layoutGauge(box, dials, rows, state.gauge_layout, m);
    ctx.textBaseline = 'middle';
    for (const p of lay.panels) {
      ctx.strokeStyle = col.border;
      ctx.lineWidth = px;
      ctx.beginPath();
      ctx.roundRect(p.rect.x + px / 2, p.rect.y + px / 2, p.rect.w - px, p.rect.h - px, 4 * px);
      ctx.stroke();
      ctx.textAlign = 'left';
      ctx.font = `600 ${fs}px ${fonts.sans}`;
      ctx.fillStyle = col.textStrong;
      const title = clipText(ctx, p.title, p.rect.w - m.pad * 2 - (p.sub ? fs * 4 : 0));
      ctx.fillText(title, p.rect.x + m.pad, p.rect.y + m.titleH / 2);
      if (p.sub) {
        const tw = ctx.measureText(title).width;
        ctx.font = `400 ${fs * 0.9}px ${fonts.sans}`;
        ctx.fillStyle = col.text;
        ctx.fillText(p.sub, p.rect.x + m.pad + tw + fs * 0.5, p.rect.y + m.titleH / 2);
      }
    }
    for (const h of lay.headers) {
      ctx.textAlign = 'center';
      ctx.fillStyle = col.textStrong;
      const label = h.sub ? `${h.text} (${h.sub})` : h.text;
      // A heading shrinks a little before it is cut: a narrow column should
      // still say which dial it is.
      ctx.font = `600 ${fs}px ${fonts.sans}`;
      const w = ctx.measureText(label).width;
      if (w > h.w) ctx.font = `600 ${Math.max(fs * 0.75, (fs * h.w) / w)}px ${fonts.sans}`;
      ctx.fillText(clipText(ctx, label, h.w), h.x + h.w / 2, h.y + m.titleH / 2);
    }
    for (const n of lay.rowNames) {
      ctx.textAlign = 'left';
      ctx.font = `600 ${fs}px ${fonts.sans}`;
      ctx.fillStyle = col.textStrong;
      ctx.fillText(clipText(ctx, n.text, n.w), n.x, n.y);
    }
    for (const n of lay.notes) {
      ctx.textAlign = 'center';
      ctx.font = `400 ${fs}px ${fonts.sans}`;
      ctx.fillStyle = col.text;
      ctx.fillText(clipText(ctx, n.text, n.rect.w), n.rect.x + n.rect.w / 2, n.rect.y + n.rect.h / 2);
    }
    for (const cell of lay.cells) {
      drawDial(ctx, cell, dials[cell.dial]!, rows[cell.row]!.readings[cell.dial]!, ranges[cell.dial]!, col, fonts, px);
    }
    ctx.restore();
  };
  const accessible = describeReadings(dials, rows);
  return {
    uData: [[0, 1], [null, null]],
    opts: {
      ...size,
      padding: [0, 0, 0, 0],
      scales: { x: { time: false, range: () => [0, 1] }, y: { range: () => [0, 1] } },
      axes: [{ show: false }, { show: false }],
      series: [{}, { paths: () => null, points: { show: false } }],
      legend: { show: false }, cursor: { show: false },
      hooks: { draw: [draw], ready: [(u) => {
        u.root.setAttribute('role', 'img');
        u.root.setAttribute('aria-label', accessible || 'Gauge');
        u.root.title = data.warning || accessible;
      }] },
    },
  };
}

export function renderGauge(data: GaugeResponse): void {
  const { opts, uData } = buildGaugeOpts(sliceGauge(data), themeColors(), chartContainerSize(), plotState.value, true, data);
  rebuildUplot(uData, opts);
  colorLegend.set(null);
}
